// Escalating wake — the pure decisions behind "still asleep?".
//
// Two consumers, one set of rules:
//   • ProtocolAlarmManager (in-app, app-open): if auto:wake fires and is neither
//     Done nor Dismissed within a few minutes, re-fire louder, and offer a
//     Snooze that RE-ARMS (a fresh timer) rather than a one-shot.
//   • protocol-dispatcher (server): if auto:wake was never marked done by
//     leave-time, send ONE backup wake push (its own step_key idempotency lock).
//
// All pure and bounded — no timers, no DOM, no network — so both consumers make
// the same call and the tests can drive the clock directly.

export const ESCALATION_DEFAULTS = Object.freeze({
  REFIRE_INTERVAL_MS: 3 * 60 * 1000, // re-fire an unattended wake every 3 min
  MAX_REFIRES: 3,                    // …up to 3 times, then stop nagging
  BACKUP_WINDOW_MIN: 60,             // server backup fires within 1h of leave-time
});

// In-app: should an already-fired, still-open wake alarm re-fire (louder)?
// Re-fires only when it is genuinely unattended: not done, not dismissed, the
// interval has elapsed since the last fire, and we haven't hit the cap.
export function shouldReFireWake({
  nowMs, lastFiredMs, status, dismissed, refireCount,
  intervalMs = ESCALATION_DEFAULTS.REFIRE_INTERVAL_MS,
  maxRefires = ESCALATION_DEFAULTS.MAX_REFIRES,
} = {}) {
  if (status === 'done') return false;
  if (dismissed) return false;
  if (!Number.isFinite(Number(nowMs)) || !Number.isFinite(Number(lastFiredMs))) return false;
  if (Number(refireCount) >= maxRefires) return false;
  return Number(nowMs) - Number(lastFiredMs) >= intervalMs;
}

// Louder as it repeats. Level 0 = first fire; higher = more urgent. Values feed
// the WebAudio gain / beep cadence and the on-screen "still asleep?" label.
export function escalationProfile(refireCount) {
  const n = Math.max(0, Number(refireCount) || 0);
  const gain = Math.min(0.30, 0.12 + n * 0.09);   // 0.12 → 0.21 → 0.30 (capped)
  const beepMs = Math.max(160, 350 - n * 90);      // 350 → 260 → 170 → 160
  const label = n === 0 ? '' : n === 1 ? 'Still time — but move now' : 'Still asleep? Route day today';
  return { level: n, gain, beepMs, label };
}

// Snooze RE-ARMS: returns the next fire instant (now + snoozeMin), never a
// terminal state. The caller must NOT record a dismissal for a snooze.
export function nextSnoozeFireMs(nowMs, snoozeMin = 9) {
  const now = Number(nowMs);
  const mins = Number(snoozeMin);
  if (!Number.isFinite(now) || !Number.isFinite(mins) || mins <= 0) return null;
  return now + Math.round(mins) * 60 * 1000;
}

// Server: is a backup wake push due now? True only when wake is still not done
// and we're inside the window that opens at leave-time. The dispatcher's
// step_key='auto:wake_backup' insert lock guarantees it fires at most once.
export function backupWakeDue({
  nowMin, leaveMin, wakeDone,
  windowMin = ESCALATION_DEFAULTS.BACKUP_WINDOW_MIN,
} = {}) {
  const now = Number(nowMin);
  const leave = Number(leaveMin);
  if (wakeDone) return false;
  if (!Number.isFinite(now) || !Number.isFinite(leave)) return false;
  return now >= leave && now < leave + windowMin;
}
